/** The Kanban board, card drawer, subtasks and comments (§4.7). */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { AlertTriangle, CalendarClock, Check, MessageSquare, Plus, User } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate, dateTime } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  inputClass,
} from '../../components/ui';
import { ProgressBar, STATUS_TONES } from './ProjectsPage';
import { ProjectCommissionsCard, type CommissionRow } from '../leads/CommissionsCard';

type Column = 'TODO' | 'DOING' | 'HOLD' | 'COMPLETED';

const COLUMNS: { key: Column; label: string; hint: string }[] = [
  { key: 'TODO', label: 'To do', hint: 'Not started' },
  { key: 'DOING', label: 'Doing', hint: 'In progress' },
  { key: 'HOLD', label: 'Hold', hint: 'Blocked — reason required' },
  { key: 'COMPLETED', label: 'Completed', hint: 'Ready to bill' },
];

interface Subtask {
  id: string;
  title: string;
  done: boolean;
  position: number;
}

interface DraftSubtask {
  id: string;
  title: string;
  done: boolean;
  _new?: boolean;
  _deleted?: boolean;
}

interface CardRow {
  id: string;
  title: string;
  description: string | null;
  column: Column;
  position: number;
  dueDate: string | null;
  holdReason: string | null;
  amountVatEx: string;
  billed: boolean;
  assignee: { id: string; name: string } | null;
  subtasks: Subtask[];
  _count: { comments: number };
}

interface ProjectDetail {
  id: string;
  code: string;
  name: string;
  status: 'PENDING' | 'ONGOING' | 'COMPLETED' | 'CANCELLED';
  startDate: string | null;
  targetEndDate: string | null;
  cancelReason: string | null;
  pm: { id: string; name: string } | null;
  members: { user: { id: string; name: string } }[];
  salesOrder: { id: string; code: string; customer: { id: string; name: string } };
  deliverables: CardRow[];
  progress: { done: number; total: number; pct: number };
  estimatedCost?: string;
  actualCost?: string;
  costVariance?: string;
  apEntries?: { id: string; code: string; payee: string; amount: string; status: string; entryDate: string; isCommission?: boolean }[];
  /** The deal's commissions (with leads.view_commission) and the payable each became. */
  commissions?: CommissionRow[];
  estimatedCommission?: string;
  totalCostWithCommission?: string;
}

function KanbanCard({ card, onOpen }: { card: CardRow; onOpen: () => void }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.id });
  const done = card.subtasks.filter((subtask) => subtask.done).length;
  const overdue = card.dueDate && card.column !== 'COMPLETED' && new Date(card.dueDate) < new Date();

  return (
    <div
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900 ${
        isDragging ? 'opacity-60 ring-2 ring-brand' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        <button
          className="flex-1 cursor-grab text-left active:cursor-grabbing"
          {...listeners}
          {...attributes}
        >
          <p className="text-sm font-medium">{card.title}</p>
        </button>
        <button
          onClick={onOpen}
          className="rounded-lg px-2 py-1 text-xs text-brand hover:bg-brand-50 dark:hover:bg-slate-800"
        >
          Open
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {card.assignee && (
          <span className="inline-flex items-center gap-1">
            <User aria-hidden="true" className="h-3 w-3" />
            {card.assignee.name.split(' ')[0]}
          </span>
        )}
        {card.dueDate && (
          <span className={`inline-flex items-center gap-1 ${overdue ? 'font-semibold text-red-600' : ''}`}>
            <CalendarClock aria-hidden="true" className="h-3 w-3" />
            {shortDate(card.dueDate)}
          </span>
        )}
        {card.subtasks.length > 0 && (
          <span className="tabular">
            {done}/{card.subtasks.length}
          </span>
        )}
        {card._count.comments > 0 && (
          <span className="inline-flex items-center gap-1">
            <MessageSquare aria-hidden="true" className="h-3 w-3" />
            {card._count.comments}
          </span>
        )}
        {card.column === 'HOLD' && <StatusChip tone="warning">On hold</StatusChip>}
        {card.billed && <StatusChip tone="ok">Billed</StatusChip>}
      </div>
    </div>
  );
}

function KanbanColumn({
  column,
  cards,
  onOpen,
}: {
  column: (typeof COLUMNS)[number];
  cards: CardRow[];
  onOpen: (card: CardRow) => void;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-64 w-full flex-col gap-2 rounded-2xl border p-3 ${
        isOver
          ? 'border-brand bg-brand-50 dark:bg-slate-800'
          : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50'
      }`}
    >
      <div className="mb-1">
        <p className="text-sm font-bold text-navy dark:text-white">
          {column.label}
          <span className="ml-2 text-xs font-normal text-slate-500">{cards.length}</span>
        </p>
        <p className="text-xs text-slate-500">{column.hint}</p>
      </div>
      {cards.map((card) => (
        <KanbanCard key={card.id} card={card} onOpen={() => onOpen(card)} />
      ))}
      {cards.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 p-3 text-center text-xs text-slate-400 dark:border-slate-700">
          Drop a card here
        </p>
      )}
    </div>
  );
}

export function ProjectDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [openCard, setOpenCard] = useState<CardRow | null>(null);
  const [newSubtask, setNewSubtask] = useState('');
  /**
   * Subtask edits are held here until Save changes, so Cancel really does
   * discard them. `_new` rows do not exist on the server yet; `_deleted` ones
   * are removed on save.
   */
  const [draft, setDraft] = useState<DraftSubtask[]>([]);
  const [savingSubtasks, setSavingSubtasks] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [adding, setAdding] = useState(false);
  const [newCard, setNewCard] = useState({ title: '', description: '', dueDate: '' });
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const project = useQuery({
    queryKey: ['project', id],
    queryFn: () => api<{ data: ProjectDetail }>(`/projects/${id}`),
  });

  const comments = useQuery({
    queryKey: ['card-comments', openCard?.id],
    queryFn: () =>
      api<{ data: { id: string; body: string; createdAt: string; user: { name: string } }[] }>(
        `/projects/cards/${openCard?.id}/comments`,
      ),
    enabled: Boolean(openCard),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['project', id] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  /** Opening a card copies its subtasks into the draft. */
  const openDrawer = (card: CardRow): void => {
    setOpenCard(card);
    setNewSubtask('');
    setDraft(
      card.subtasks.map((subtask) => ({
        id: subtask.id,
        title: subtask.title,
        done: subtask.done,
      })),
    );
  };

  const serverSubtasks = (): DraftSubtask[] =>
    (row?.deliverables.find((card) => card.id === openCard?.id)?.subtasks ?? []).map((subtask) => ({
      id: subtask.id,
      title: subtask.title,
      done: subtask.done,
    }));

  const draftIsDirty = (): boolean => {
    const original = serverSubtasks();
    const live = draft.filter((subtask) => !subtask._deleted);
    if (live.length !== original.length) return true;
    return live.some((subtask, index) => {
      const before = original[index];
      return !before || before.id !== subtask.id || before.title !== subtask.title || before.done !== subtask.done;
    });
  };

  const closeDrawer = (): void => {
    if (draftIsDirty()) {
      const leave = window.confirm('Discard the changes to these subtasks?');
      if (!leave) return;
    }
    setOpenCard(null);
    setDraft([]);
    setNewSubtask('');
  };

  /** Applies the draft: adds, renames, ticks and deletes, then closes. */
  const saveSubtasks = async (): Promise<void> => {
    if (!openCard) return;
    setSavingSubtasks(true);
    setError(null);
    try {
      const original = serverSubtasks();

      for (const subtask of draft) {
        if (subtask._new && !subtask._deleted) {
          const created = await api<{ data: { id: string } }>(
            `/projects/cards/${openCard.id}/subtasks`,
            { method: 'POST', body: { title: subtask.title.trim() } },
          );
          // A row added and ticked in the same sitting needs both calls.
          if (subtask.done) {
            await api(`/projects/subtasks/${created.data.id}`, {
              method: 'PATCH',
              body: { done: true },
            });
          }
          continue;
        }

        if (subtask._deleted && !subtask._new) {
          await api(`/projects/subtasks/${subtask.id}`, { method: 'DELETE' });
          continue;
        }

        const before = original.find((entry) => entry.id === subtask.id);
        if (!before) continue;
        const changes: Record<string, unknown> = {};
        if (before.title !== subtask.title.trim()) changes.title = subtask.title.trim();
        if (before.done !== subtask.done) changes.done = subtask.done;
        if (Object.keys(changes).length > 0) {
          await api(`/projects/subtasks/${subtask.id}`, { method: 'PATCH', body: changes });
        }
      }

      setOpenCard(null);
      setDraft([]);
      setNewSubtask('');
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the subtasks.');
    } finally {
      setSavingSubtasks(false);
    }
  };

  const move = useMutation({
    mutationFn: (input: {
      cardId: string;
      column: Column;
      position: number;
      holdReason?: string;
      completeSubtasks?: boolean;
    }) =>
      api(`/projects/cards/${input.cardId}/move`, {
        method: 'POST',
        body: {
          column: input.column,
          position: input.position,
          holdReason: input.holdReason,
          completeSubtasks: input.completeSubtasks,
        },
      }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not move the card.'),
  });

  const addCard = useMutation({
    mutationFn: () =>
      api(`/projects/${id}/cards`, {
        method: 'POST',
        body: {
          title: newCard.title.trim(),
          description: newCard.description || undefined,
          dueDate: newCard.dueDate || undefined,
        },
      }),
    onSuccess: () => {
      setAdding(false);
      setNewCard({ title: '', description: '', dueDate: '' });
      refresh();
    },
  });

  const addComment = useMutation({
    mutationFn: () =>
      api(`/projects/cards/${openCard?.id}/comments`, {
        method: 'POST',
        body: { body: newComment.trim() },
      }),
    onSuccess: () => {
      setNewComment('');
      void queryClient.invalidateQueries({ queryKey: ['card-comments', openCard?.id] });
      refresh();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: ProjectDetail['status']) =>
      api(`/projects/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the status.'),
  });

  const row = project.data?.data;

  const byColumn = useMemo(() => {
    const grouped: Record<Column, CardRow[]> = { TODO: [], DOING: [], HOLD: [], COMPLETED: [] };
    for (const card of row?.deliverables ?? []) grouped[card.column].push(card);
    for (const key of Object.keys(grouped) as Column[]) {
      grouped[key].sort((a, b) => a.position - b.position);
    }
    return grouped;
  }, [row]);

  if (project.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!row) return <Alert tone="critical">Project not found.</Alert>;

  const onDragEnd = (event: DragEndEvent): void => {
    const cardId = String(event.active.id);
    const target = event.over?.id as Column | undefined;
    if (!target) return;
    const card = row.deliverables.find((item) => item.id === cardId);
    if (!card || card.column === target) return;

    if (target === 'HOLD') {
      const reason = window.prompt('Why is this deliverable on hold?');
      if (!reason) return;
      move.mutate({ cardId, column: target, position: byColumn[target].length, holdReason: reason });
      return;
    }

    if (target === 'COMPLETED' && card.subtasks.some((subtask) => !subtask.done)) {
      const confirmed = window.confirm(
        'Some subtasks are not ticked. Mark all of them done and complete the card?',
      );
      if (!confirmed) return;
      move.mutate({
        cardId,
        column: target,
        position: byColumn[target].length,
        completeSubtasks: true,
      });
      return;
    }

    move.mutate({ cardId, column: target, position: byColumn[target].length });
  };

  const allDone = row.progress.total > 0 && row.progress.pct === 100;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-navy dark:text-white">{row.name}</h1>
            <StatusChip tone={STATUS_TONES[row.status]}>
              {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
            </StatusChip>
          </div>
          <p className="text-sm text-slate-500">
            <span className="font-mono">{row.code}</span> ·{' '}
            <Link to={`/sales-orders/${row.salesOrder.id}`} className="hover:underline">
              {row.salesOrder.code}
            </Link>{' '}
            ·{' '}
            <Link to={`/customers/${row.salesOrder.customer.id}`} className="hover:underline">
              {row.salesOrder.customer.name}
            </Link>
            {row.pm ? ` · managed by ${row.pm.name}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can('projects.manage') && row.status === 'PENDING' && (
            <Button onClick={() => setStatus.mutate('ONGOING')}>Start project</Button>
          )}
          {can('projects.manage') && (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" /> Add deliverable
            </Button>
          )}
        </div>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}
      {allDone && row.status === 'ONGOING' && can('projects.manage') && (
        <Alert tone="ok">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>Every deliverable is done. Mark the project completed?</span>
            <Button size="sm" onClick={() => setStatus.mutate('COMPLETED')}>
              <Check aria-hidden="true" className="h-4 w-4" /> Mark completed
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <p className="text-xs uppercase tracking-wide text-slate-500">Progress</p>
          <div className="mt-2">
            <ProgressBar pct={row.progress.pct} />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {row.progress.done} of {row.progress.total} units done
          </p>
        </Card>
        {row.estimatedCost !== undefined && (
          <>
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Estimated cost</p>
              <p className="tabular text-lg font-bold">{peso(row.estimatedCost)}</p>
            </Card>
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Actual cost</p>
              <p className="tabular text-lg font-bold">{peso(row.actualCost)}</p>
              <p className="text-xs text-slate-500">Completed payables</p>
            </Card>
            {row.estimatedCommission !== undefined && Number(row.estimatedCommission) > 0 && (
              <Card>
                <p className="text-xs uppercase tracking-wide text-slate-500">Commissions</p>
                <p className="tabular text-lg font-bold">{peso(row.estimatedCommission)}</p>
                <p className="text-xs text-slate-500">
                  Cost incl. commissions {peso(row.totalCostWithCommission)}
                </p>
              </Card>
            )}
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Variance</p>
              <p
                className={`tabular text-lg font-bold ${
                  Number(row.costVariance ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {Number(row.costVariance ?? 0) > 0 && (
                  <AlertTriangle aria-hidden="true" className="mr-1 inline h-4 w-4" />
                )}
                {peso(row.costVariance)}
              </p>
            </Card>
          </>
        )}
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map((column) => (
            <KanbanColumn
              key={column.key}
              column={column}
              cards={byColumn[column.key]}
              onOpen={openDrawer}
            />
          ))}
        </div>
      </DndContext>

      {row.apEntries && row.apEntries.length > 0 && (
        <Card title="Costs charged to this project">
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {row.apEntries.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="font-mono text-xs">{entry.code}</span>
                <span className="flex-1">
                  {entry.payee}
                  {entry.isCommission && <span className="ml-2 text-xs text-violet-700 dark:text-violet-300">commission</span>}
                </span>
                <span className="text-xs text-slate-500">{shortDate(entry.entryDate)}</span>
                <StatusChip tone={entry.status === 'COMPLETED' ? 'ok' : 'warning'}>
                  {entry.status.charAt(0) + entry.status.slice(1).toLowerCase()}
                </StatusChip>
                <span className="tabular font-semibold">{peso(entry.amount)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ProjectCommissionsCard rows={row.commissions} />

      {openCard && (
        <Modal title={openCard.title} onClose={closeDrawer}>
          <div className="space-y-5">
            {openCard.description && (
              <p className="text-sm text-slate-600 dark:text-slate-300">{openCard.description}</p>
            )}
            <div className="flex flex-wrap gap-3 text-sm">
              <StatusChip tone="neutral">
                {COLUMNS.find((column) => column.key === openCard.column)?.label}
              </StatusChip>
              {openCard.assignee && <StatusChip tone="info">{openCard.assignee.name}</StatusChip>}
              {openCard.dueDate && <StatusChip tone="neutral">Due {shortDate(openCard.dueDate)}</StatusChip>}
              {openCard.holdReason && <StatusChip tone="warning">Hold: {openCard.holdReason}</StatusChip>}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold">Subtasks</p>
                {draftIsDirty() && <StatusChip tone="warning">Unsaved changes</StatusChip>}
              </div>

              <ul className="space-y-1">
                {draft
                  .filter((subtask) => !subtask._deleted)
                  .map((subtask) => (
                    <li key={subtask.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                        checked={subtask.done}
                        disabled={!can('kanban.update')}
                        onChange={(event) =>
                          setDraft((current) =>
                            current.map((entry) =>
                              entry.id === subtask.id
                                ? { ...entry, done: event.target.checked }
                                : entry,
                            ),
                          )
                        }
                      />
                      {can('kanban.update') ? (
                        <input
                          className={`${inputClass} border-transparent bg-transparent px-1 py-0.5 text-sm ${
                            subtask.done ? 'text-slate-400 line-through' : ''
                          }`}
                          value={subtask.title}
                          onChange={(event) =>
                            setDraft((current) =>
                              current.map((entry) =>
                                entry.id === subtask.id
                                  ? { ...entry, title: event.target.value }
                                  : entry,
                              ),
                            )
                          }
                        />
                      ) : (
                        <span className={subtask.done ? 'text-slate-400 line-through' : ''}>
                          {subtask.title}
                        </span>
                      )}
                      {can('kanban.update') && (
                        <button
                          className="ml-auto text-xs text-red-600 hover:underline"
                          onClick={() =>
                            setDraft((current) =>
                              subtask._new
                                ? current.filter((entry) => entry.id !== subtask.id)
                                : current.map((entry) =>
                                    entry.id === subtask.id ? { ...entry, _deleted: true } : entry,
                                  ),
                            )
                          }
                        >
                          remove
                        </button>
                      )}
                    </li>
                  ))}
                {draft.filter((subtask) => !subtask._deleted).length === 0 && (
                  <li className="text-sm text-slate-500">
                    No subtasks. This card counts as one unit of progress until you add some.
                  </li>
                )}
              </ul>

              {can('kanban.update') && (
                <form
                  className="mt-2 flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const title = newSubtask.trim();
                    if (!title) return;
                    setDraft((current) => [
                      ...current,
                      { id: `new-${Date.now()}`, title, done: false, _new: true },
                    ]);
                    setNewSubtask('');
                  }}
                >
                  <input
                    className={`${inputClass} text-sm`}
                    placeholder="Add a subtask, then press Save changes"
                    value={newSubtask}
                    onChange={(event) => setNewSubtask(event.target.value)}
                  />
                  <Button type="submit" size="sm" variant="secondary">
                    Add
                  </Button>
                </form>
              )}
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold">Notes</p>
              <ul className="space-y-2">
                {comments.data?.data.map((comment) => (
                  <li key={comment.id} className="rounded-lg bg-slate-50 p-2 text-sm dark:bg-slate-800">
                    <p>{comment.body}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {comment.user.name} · {dateTime(comment.createdAt)}
                    </p>
                  </li>
                ))}
                {comments.data?.data.length === 0 && (
                  <li className="text-sm text-slate-500">No notes yet.</li>
                )}
              </ul>
              {can('kanban.update') && (
                <form
                  className="mt-2 flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (newComment.trim()) addComment.mutate();
                  }}
                >
                  <input
                    className={`${inputClass} text-sm`}
                    placeholder="Add a note"
                    value={newComment}
                    onChange={(event) => setNewComment(event.target.value)}
                  />
                  <Button type="submit" size="sm" variant="secondary">
                    Post
                  </Button>
                </form>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Notes are posted straight away and are not affected by Save or Cancel.
              </p>
            </div>

            {can('kanban.update') && (
              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
                <Button busy={savingSubtasks} onClick={() => void saveSubtasks()}>
                  <Check aria-hidden="true" className="h-4 w-4" /> Save changes
                </Button>
                <Button variant="secondary" onClick={closeDrawer}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {adding && (
        <Modal title="Add a deliverable" onClose={() => setAdding(false)}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              addCard.mutate();
            }}
          >
            <Field label="Title" required>
              <input
                className={inputClass}
                required
                value={newCard.title}
                onChange={(event) => setNewCard({ ...newCard, title: event.target.value })}
              />
            </Field>
            <Field label="Description">
              <textarea
                className={`${inputClass} h-20`}
                value={newCard.description}
                onChange={(event) => setNewCard({ ...newCard, description: event.target.value })}
              />
            </Field>
            <Field label="Due date">
              <input
                className={inputClass}
                type="date"
                value={newCard.dueDate}
                onChange={(event) => setNewCard({ ...newCard, dueDate: event.target.value })}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" busy={addCard.isPending}>
                Add to To do
              </Button>
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
